/* eslint-disable */
import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { usePagination, useTable } from 'react-table'
import { useTranslation } from 'react-i18next'
import { isEmpty, pathOr, propOr } from 'ramda'
import { ReactComponent as FileIcon } from 'assets/images/file-icon.svg'
import { useDispatch, useSelector } from 'react-redux'
import { getBuildingTypes } from 'features/bpList/ducks/selectors'
import { toCamelCase } from 'js-convert-case/lib'
import { removeFromCart } from 'utils/localStorage'
import { toast } from 'react-toastify'
import Tooltip from '@mui/material/Tooltip'
import { isNotNilOrEmpty } from 'utils/ramda'
import ClearIcon from '@mui/icons-material/Clear'

const ItemsTable = ({ bps }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const buildingTypes = useSelector(getBuildingTypes)
  const data = useMemo(() => bps, [bps])

  const handleRemove = value => () => {
    removeFromCart(value)
    toast.success('Building Passport was removed from your cart. You can still find it on drafts list.')
  }

  const columns = React.useMemo(
    () => [
      {
        Header: t('bpList.table.name'),
        id: 'name',
        accessor: 'name',
        Cell: d => {
          const uprn = pathOr('Not yet allocated', ['row', 'original', 'uprn'], d)
          return (
            <PropertyNameWrapper>
              <IconWrapper>
                <FileIcon />
              </IconWrapper>
              <PropertyName>
                {d.value}
                <div className='uprn'>
                  UPRN: {
                  isNotNilOrEmpty(uprn)
                    ? uprn
                    : 'Not yet allocated'
                }
                </div>
              </PropertyName>
            </PropertyNameWrapper>
          )
        }
      },
      {
        Header: 'type',
        accessor: 'buildingTypes',
        Cell: d => {
          return d.value.map((e, index) => {
            const type = propOr(
              { label: '', unit: '' },
              toCamelCase(e.type),
              buildingTypes)
            return <Bold key={index}>{type.label}</Bold>
          })
        }
      },
      {
        Header: 'Billing',
        id: 'billing',
        Cell: d => {
          const isOneTime = d.row.original.billingType === 'one_time'

          return (
            <BillingCell>
              <BillingBadge $isOneTime={isOneTime}>
                {isOneTime ? 'One-time' : 'Annual subscription'}
              </BillingBadge>
              <BillingDescription>
                {isOneTime
                  ? 'Lifetime access'
                  : 'Renews every year'}
              </BillingDescription>
            </BillingCell>
          )
        }
      },
      {
        Header: 'Area / Units',
        id: 'lettableArea',
        accessor: 'owner',
        Cell: d => {
          return d.row.original.buildingTypes.map((e, index) => {
            const type = propOr(
              { label: '', unit: '' },
              toCamelCase(e.type),
              buildingTypes)
            return (
              <div key={index}>
                {`${type.unit === 'sqm' ? e.area : e.units} ${type.unit}`}
              </div>
            )
          })
        }
      },
      {
        Header: 'price',
        accessor: 'price',
        Cell: d => {
          const isMinimalPriceReached = pathOr(false, ['row', 'original', 'isMinimalPriceReached'], d)
          return isMinimalPriceReached
            ? d.row.original.buildingTypes.map((e, index) => {
              const price = propOr(0, 'netPrice', e)
              return <div key={index}>{price.formatted}</div>
            })
            : <div>Applicable for minimum price</div>
        }
      },
      {
        Header: 'total',
        accessor: 'netPrice',
        Cell: d => {
          const isAnnualSubscription =
            d.row.original.billingType === 'subscription' &&
            d.row.original.billingInterval === 'year'

          return (
            <Bold>
              {d.value.formatted}
              {isAnnualSubscription && <PriceInterval>/year</PriceInterval>}
            </Bold>
          )
        }
      },
      {
        Header: '',
        accessor: 'id',
        Cell: d => {
          return (
            <Tooltip
              title='Clicking “remove” will remove this Building Passport
               from the basket. It will still be available in your Drafts.'
            >
              <DeleteIconWrapper onClick={handleRemove(d.value)}>
                <span>REMOVE</span>
                <CrossIcon />
              </DeleteIconWrapper>
            </Tooltip>
          )
        }
      }
    ],
    [bps, buildingTypes]
  )

  const tableInstance = useTable({
    columns,
    data
  })

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = tableInstance

  return (
    <TableWrapper>
      <table {...getTableProps()}>
        <thead>
        {
          headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {
                headerGroup.headers.map(column => (
                  <th
                    {...column.getHeaderProps()}
                  >
                    {column.render('Header')}
                    <span>
                  </span>
                  </th>
                ))
              }
            </tr>
          ))
        }
        </thead>
        <tbody {...getTableBodyProps()}>
        {
          rows.map(row => {
            prepareRow(row)
            return (
              <tr
                {...row.getRowProps()}
              >
                {
                  row.cells.map(cell => {
                    return (
                      <td {...cell.getCellProps()} data-label={cell.column.render('Header')}>
                        {
                          cell.render('Cell')
                        }
                      </td>
                    )
                  })}
              </tr>
            )
          })}
        </tbody>
      </table>
      {
        isEmpty(data) &&
        <EmptyState>
          No Building Passports found.
        </EmptyState>
      }
    </TableWrapper>
  )
}

export default ItemsTable

const TableWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    box-sizing: border-box;
    color: ${({ theme }) => theme.colors.darkGrey};
    font-size: 11px;
    opacity: .5;
    text-transform: uppercase;
    text-align: left;
    height: 40px;
    padding: 0 10px;
    position: relative;
  }

  td {
    box-sizing: border-box;
    padding: 20px 10px;
    height: 56px;
    font-size: 14px;
    vertical-align: middle;
  }

  tbody > tr {
    border-bottom: 2px solid ${({ theme }) => theme.colors.backgroundColor};
    background-color: ${({ theme }) => theme.colors.lighterGrey};
  }

  @media (max-width: 768px) {
    thead {
      display: none;
    }

    tbody > tr {
      display: block;
      margin-bottom: 20px;
      border: 1px solid ${({ theme }) => theme.colors.backgroundColor};
      border-radius: 8px;
      background-color: ${({ theme }) => theme.colors.lighterGrey};
      padding: 10px;
    }

    td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: auto;
      padding: 10px;
      text-align: right;
      border-bottom: 1px solid rgba(0,0,0,0.05);
    }

    td:last-child {
      border-bottom: none;
      justify-content: flex-end;
    }

    td:before {
      content: attr(data-label);
      font-weight: bold;
      text-transform: uppercase;
      font-size: 11px;
      color: ${({ theme }) => theme.colors.darkGrey};
      opacity: 0.7;
      text-align: left;
      margin-right: 15px;
    }
  }
`

const PropertyNameWrapper = styled.div`
  display: flex;
  align-items: center;
`

const PropertyName = styled.div`
  font-size: 19px;
  color: ${({ theme }) => theme.colors.haiti};
  text-align: left;

  .uprn {
    margin-top: 3px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.grey[700]};
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const EmptyState = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100px;
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
`

const IconWrapper = styled.div`
  min-width: 43px;
  min-height: 43px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fbede9;
  margin-right: 15px;
  border-radius: 4px;
`

const Bold = styled.div`
  font-weight: bold;
`

const BillingCell = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 5px;
`

const BillingBadge = styled.span`
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  color: ${({ $isOneTime }) => $isOneTime ? '#166534' : '#33becc'};
  background: ${({ $isOneTime }) => $isOneTime ? '#dcfce7' : '#e6f7f9'};
`

const BillingDescription = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const PriceInterval = styled.span`
  margin-left: 3px;
  font-size: 11px;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const DeleteIconWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.error};

  span {
    transition: all .3s;
    opacity: 0;
    margin-right: 3px;

    @media (max-width: 768px) {
      opacity: 1;
    }
  }

  &:hover {
    span {
      opacity: 1;
    }
  }
`

const CrossIcon = styled(ClearIcon)`
  font-size: 16px !important;
  color: ${({ disabled, theme }) =>  disabled ? theme.colors.grey[600] : theme.colors.primary};
`
